import { ArrowDropUp, ArrowDropDown } from '@mui/icons-material';
import { Alert, Box, Checkbox, FormControlLabel, Grid, IconButton, Tooltip, Typography } from '@mui/material';
import { Controller, useFieldArray, useFormContext } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import ControlledCheckBox from 'src/shared/components/form/controlledCheckbox';
import { AvailablePaymentMethodsEnum, getPaymentMethodDescription } from 'src/shared/models/enums/availablePaymentMethods.enum';
import { PaymentMethods } from 'src/shared/models/invoiceGroup/invoiceGroup';

type FormValues = {
  paymentMethods: PaymentMethods[];
  preSelect: boolean;
};

type ActivePaymentOptionsInfoProps = {
  disabled?: boolean;
  eInvoiceSenderConnected?: boolean;
  electronicInvoiceEmailExists?: boolean;
};

export const ActivePaymentOptionsInfo = ({
  disabled = true,
  eInvoiceSenderConnected = false,
  electronicInvoiceEmailExists = false,
}: ActivePaymentOptionsInfoProps) => {
  const { t } = useTranslation();

  const { control, setValue, getValues, watch } = useFormContext<FormValues>();
  const { fields, move } = useFieldArray({
    control,
    name: 'paymentMethods',
  });

  const syncPositions = () => {
    const items = getValues('paymentMethods') as PaymentMethods[] | undefined;
    if (!items) return;

    items.forEach((_, index) => {
      setValue(`paymentMethods.${index}.pos`, index + 1);
    });
  };

  const handleMoveUp = (index: number) => {
    if (index === 0) return;
    move(index, index - 1);
    syncPositions();
  };

  const handleMoveDown = (index: number) => {
    if (index === fields.length - 1) return;
    move(index, index + 1);
    syncPositions();
  };

  return (
    <Grid container spacing={2}>
      <Grid item xs={12} mb={-1} mt={-1}>
        <Typography variant='h6' sx={{ fontWeight: 'bold', mb: 1 }}>
          {t('invoice:adminInvoiceGroupDetailPage.activePaymentsOpt')}
        </Typography>
      </Grid>

      <Grid item xs={12} mb={-1} mt={-1}>
        <Grid container alignItems='center' sx={{ mb: 0.5, px: 0.5 }}>
          <Grid item xs={6} lg={5}>
            <Typography variant='caption' color='text.secondary'>
              {t('invoice:adminInvoiceGroupDetailPage.active')}
            </Typography>
          </Grid>
          <Grid item xs={6} lg={7}>
            <Typography variant='caption' color='text.secondary'>
              {t('invoice:adminInvoiceGroupDetailPage.order')}
            </Typography>
          </Grid>
        </Grid>

        {fields.map((method, index) => {
          const activeVal = watch(`paymentMethods.${index}.active`);
          const isEInvoice = method.type === AvailablePaymentMethodsEnum.ElectronicInvoice;
          const eInvoiceDisabled = isEInvoice && (!eInvoiceSenderConnected || !electronicInvoiceEmailExists);

          return (
            <Grid container alignItems='center' key={method.id} sx={{ mb: { xs: 0, sm: 0, md: 0, lg: 1 } }}>
              <Grid item xs={6} sm={6} md={6} lg={5} mb={-0.5}>
                <Tooltip
                  title={(() => {
                    if (isEInvoice && !eInvoiceSenderConnected) return t('invoice:adminInvoiceGroupDetailPage.eInvoiceSenderRequiredTooltip');
                    if (isEInvoice && !electronicInvoiceEmailExists) return t('invoice:adminInvoiceGroupDetailPage.eInvoiceEmailRequiredTooltip');
                    return '';
                  })()}
                  arrow
                >
                  <span>
                    <ControlledCheckBox
                      label={getPaymentMethodDescription(method.type, t)}
                      name={`paymentMethods.${index}.active`}
                      value={activeVal}
                      disabled={disabled || eInvoiceDisabled}
                    />
                  </span>
                </Tooltip>
              </Grid>
              <Grid item xs={6} lg={7}>
                <Box sx={{ display: 'flex', alignItems: 'center' }}>
                  <IconButton sx={{ mr: -1 }} onClick={() => handleMoveUp(index)} disabled={disabled || index === 0}>
                    <ArrowDropUp />
                  </IconButton>
                  <IconButton onClick={() => handleMoveDown(index)} disabled={disabled || index === fields.length - 1}>
                    <ArrowDropDown />
                  </IconButton>
                  {index === 0 && (
                    <Controller
                      name='preSelect'
                      control={control}
                      render={({ field }) => (
                        <FormControlLabel
                          sx={{ ml: 0.5 }}
                          control={
                            <Checkbox checked={!!field.value} onChange={(e) => field.onChange(e.target.checked)} disabled={disabled} size='small' />
                          }
                          label={<span>{t('invoice:adminInvoiceGroupDetailPage.preSelectFirst')}</span>}
                        />
                      )}
                    />
                  )}
                </Box>
              </Grid>
            </Grid>
          );
        })}
      </Grid>

      <Grid item xs={12} sm={10} mt={1}>
        <Alert sx={{ fontWeight: 600 }} severity='info'>
          {t('invoice:adminInvoiceGroupDetailPage.paymentOptDescription')}
        </Alert>
      </Grid>
    </Grid>
  );
};
